import { useEffect, useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/context/AuthContext";
import { buildEmbedQuery } from "@/components/MapDialog";
import WorkerAvatar from "@/components/WorkerAvatar";
import { ACTION_META, ChangeList } from "@/components/HistoryView";
import { hitungMasaKerja, hitungUsia, formatWaktu } from "@/lib/dates";
import { isSertifikat, labelSertifikat, statusSertifikat } from "@/lib/sertifikat";
import SertifikatBerlakuBadge from "@/components/workers/SertifikatBerlakuBadge";
import {
  Briefcase, MapPin, CalendarDays, Cake, GraduationCap, Users, CreditCard,
  HeartPulse, HardHat, Home, ExternalLink, Pencil, Clock, Loader2, Printer,
  Paperclip, Download, Trash2, FileText, Eye, Award,
} from "lucide-react";
import ZoomImage from "@/components/ui/zoom-image";

const JENIS_DOKUMEN = ["KTP", "Ijazah", "Kontrak Kerja", "BPJS", "Lainnya"];

const statusTone = (s) => {
  const v = (s || "").toUpperCase();
  if (v === "AKTIF") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (v === "PENSIUN") return "bg-amber-50 text-amber-700 border-amber-200";
  if (v === "RESIGN") return "bg-slate-100 text-slate-700 border-slate-200";
  if (v === "PHK") return "bg-rose-50 text-rose-700 border-rose-200";
  return "bg-slate-100 text-slate-600 border-slate-200";
};

const fmtDate = (d) => {
  if (!d) return "-";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d));
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const date = new Date(d);
  if (isNaN(date)) return d;
  const p = (n) => String(n).padStart(2, "0");
  return `${p(date.getDate())}/${p(date.getMonth() + 1)}/${date.getFullYear()}`;
};

const Item = ({ icon: Icon, label, value, testid }) => (
  <div className="flex items-start gap-3" data-testid={testid}>
    <div className="h-9 w-9 rounded-md bg-slate-50 border border-slate-200 flex items-center justify-center shrink-0">
      <Icon className="h-4 w-4 text-blue-600" />
    </div>
    <div className="min-w-0">
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
      <p className="text-sm font-medium text-slate-900 break-words">{value || "-"}</p>
    </div>
  </div>
);

const SectionTitle = ({ children }) => (
  <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 border-b border-slate-100 pb-2">{children}</h4>
);

export default function WorkerDetailDialog({ open, onOpenChange, worker, onEdit, readOnly = false }) {
  const [logs, setLogs] = useState(null);
  const [printing, setPrinting] = useState(false);
  const [dokumen, setDokumen] = useState([]);
  const [jenis, setJenis] = useState("KTP");
  const [uploadingDok, setUploadingDok] = useState(false);
  const [preview, setPreview] = useState(null);
  const dokInputRef = useRef(null);
  const workerId = worker?.id;

  useEffect(() => {
    if (open && workerId) {
      setLogs(null);
      setDokumen(worker?.dokumen || []);
      api.get("/audit-logs", { params: { worker_id: workerId, limit: 50 } })
        .then((res) => setLogs(res.data))
        .catch(() => setLogs([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, workerId]);

  if (!worker) return null;

  const printCard = async () => {
    setPrinting(true);
    try {
      const res = await api.get(`/workers/${worker.id}/card`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `kartu_${worker.nomor_pegawai || "pekerja"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 2000);
    } finally {
      setPrinting(false);
    }
  };

  const uploadDok = async (e) => {
    const file = e.target.files?.[0];
    if (dokInputRef.current) dokInputRef.current.value = "";
    if (!file) return;
    const ok = ["application/pdf", "image/jpeg", "image/png", "image/webp"];
    if (!ok.includes(file.type)) { toast.error("Dokumen harus PDF, JPG, PNG, atau WEBP"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Ukuran dokumen maksimal 10 MB"); return; }
    setUploadingDok(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/workers/${worker.id}/dokumen`, fd, {
        params: { jenis },
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDokumen(res.data.dokumen);
      toast.success(`Dokumen ${jenis} berhasil diunggah`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengunggah dokumen");
    } finally {
      setUploadingDok(false);
    }
  };

  const downloadDok = async (d) => {
    try {
      const res = await api.get(`/workers/${worker.id}/dokumen/${d.id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.nama_file || "dokumen";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 2000);
    } catch {
      toast.error("Gagal mengunduh dokumen");
    }
  };

  const previewDok = async (d) => {
    try {
      const res = await api.get(`/workers/${worker.id}/dokumen/${d.id}`, { responseType: "blob" });
      const type = res.data.type || "";
      const url = window.URL.createObjectURL(res.data);
      setPreview({ url, isPdf: type.includes("pdf"), nama: d.nama_file, jenis: d.jenis });
    } catch {
      toast.error("Gagal membuka pratinjau dokumen");
    }
  };

  const closePreview = () => {
    if (preview?.url) window.URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const deleteDok = async (d) => {
    try {
      const res = await api.delete(`/workers/${worker.id}/dokumen/${d.id}`);
      setDokumen(res.data.dokumen);
      toast.success("Dokumen dihapus");
    } catch {
      toast.error("Gagal menghapus dokumen");
    }
  };

  const fmtUkuran = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
  const query = buildEmbedQuery(worker);
  const embedSrc = `https://www.google.com/maps?q=${encodeURIComponent(query)}&z=12&output=embed`;
  const link = (worker.link_maps || "").trim();
  const externalHref = /^https?:\/\//i.test(link)
    ? link
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="worker-detail-dialog">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3 pr-6">
            <div className="flex items-center gap-4">
              <WorkerAvatar worker={worker} size="h-14 w-14" textSize="text-base" />
              <div>
                <DialogTitle className="text-xl" data-testid="detail-nama">{worker.nama}</DialogTitle>
                <DialogDescription data-testid="detail-nomor">
                  {worker.nomor_pegawai} &middot; {worker.jabatan}
                </DialogDescription>
              </div>
            </div>
            <Badge variant="outline" className={`${statusTone(worker.status)} font-medium shrink-0`} data-testid="detail-status">
              {worker.status || "-"}
            </Badge>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-3">
            <SectionTitle>Informasi Pekerjaan</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Item icon={Briefcase} label="Jabatan" value={worker.jabatan} testid="detail-jabatan" />
              <Item icon={Briefcase} label="Level Pekerja" value={worker.level_pekerja} testid="detail-level" />
              <Item icon={MapPin} label="Lokasi Kerja" value={worker.lokasi_kerja} testid="detail-lokasi" />
              <Item icon={CalendarDays} label="Tanggal Masuk" value={fmtDate(worker.tanggal_masuk)} testid="detail-tanggal-masuk" />
              <Item icon={Clock} label="Masa Kerja" value={hitungMasaKerja(worker.tanggal_masuk)} testid="detail-masa-kerja" />
              <Item icon={Cake} label="Usia" value={(() => { const u = hitungUsia(worker.tanggal_lahir) ?? (worker.usia !== "" && worker.usia != null ? Number(worker.usia) : null); return u != null ? `${u} tahun` : "-"; })()} testid="detail-usia" />
              <Item icon={Award} label="Sertifikasi" value={(() => { const s = dokumen.filter(isSertifikat); return s.length ? `${s.length} sertifikat` : (worker.punya_sertifikasi ? "Ada (belum diunggah)" : "Tidak ada"); })()} testid="detail-sertifikasi-ringkas" />
              <Item icon={CalendarDays} label="Tanggal Akhir Kontrak" value={fmtDate(worker.tanggal_akhir_kontrak)} testid="detail-akhir-kontrak" />
              <Item icon={Users} label="Pilihan Opsi" value={worker.pilihan_opsi} testid="detail-opsi" />
            </div>
          </div>

          <div className="space-y-3">
            <SectionTitle>Perlengkapan &amp; Pertalife</SectionTitle>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Item icon={Briefcase} label="Ukuran Baju" value={worker.ukuran_baju} testid="detail-ukuran-baju" />
              <Item icon={Briefcase} label="Ukuran Celana" value={worker.ukuran_celana} testid="detail-ukuran-celana" />
              <Item icon={Briefcase} label="Ukuran Sepatu" value={worker.ukuran_sepatu} testid="detail-ukuran-sepatu" />
              <Item icon={CreditCard} label="Nomor Pertalife" value={worker.nomor_pertalife} testid="detail-pertalife" />
            </div>
          </div>

          <div className="space-y-3">
            <SectionTitle>Sertifikasi</SectionTitle>
            {(() => { const s = dokumen.filter(isSertifikat); return s.length === 0
              ? <p className="text-sm text-slate-500" data-testid="detail-sertifikasi-kosong">{worker.punya_sertifikasi ? "Memiliki sertifikasi — belum ada dokumen diunggah." : "Tidak memiliki sertifikasi."}</p>
              : <ul className="flex flex-wrap gap-2" data-testid="detail-sertifikasi">{s.map((d) => (
                  <li key={d.id}><button type="button" onClick={() => previewDok(d)} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${["habis", "segera"].includes(statusSertifikat(d)) ? "border-rose-300 bg-rose-50 text-rose-900 hover:bg-rose-100" : "border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"}`} data-testid={`detail-sertifikat-${d.id}`}>{labelSertifikat(d)}<SertifikatBerlakuBadge dok={d} compact /></button></li>
                ))}</ul>; })()}
          </div>

          <div className="space-y-3">
            <SectionTitle>Keterangan</SectionTitle>
            <p className="text-sm text-slate-700" data-testid="detail-keterangan">{worker.keterangan || "-"}</p>
            {worker.keterangan_status && (
              <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-3" data-testid="detail-keterangan-status">
                <p className="text-xs font-bold uppercase tracking-wide text-rose-600 mb-1">
                  Keterangan {["RESIGN", "PHK"].includes((worker.status || "").toUpperCase()) ? worker.status : "Status"}
                </p>
                <p className="text-sm text-slate-700">{worker.keterangan_status}</p>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <SectionTitle>Data Pribadi</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Item icon={Cake} label="Tanggal Lahir / Usia"
                value={`${fmtDate(worker.tanggal_lahir)}${worker.usia != null && worker.usia !== "" ? ` (${worker.usia} tahun)` : ""}`}
                testid="detail-lahir" />
              <Item icon={Users} label="Gender" value={worker.gender} testid="detail-gender" />
              <Item icon={GraduationCap} label="Pendidikan" value={worker.pendidikan} testid="detail-pendidikan" />
              <Item icon={Home} label="Alamat" value={worker.alamat} testid="detail-alamat" />
            </div>
          </div>

          <div className="space-y-3">
            <SectionTitle>Identitas &amp; BPJS</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Item icon={CreditCard} label="Nomor KTP" value={worker.nomor_ktp} testid="detail-ktp" />
              <Item icon={HeartPulse} label="BPJS Kesehatan" value={worker.bpjs_kesehatan} testid="detail-bpjs-kesehatan" />
              <Item icon={HardHat} label="BPJS Ketenagakerjaan" value={worker.bpjs_ketenagakerjaan} testid="detail-bpjs-tk" />
            </div>
          </div>

          <div className="space-y-3">
            <SectionTitle>Peta Lokasi</SectionTitle>
            <div className="rounded-lg overflow-hidden border border-slate-200">
              <iframe
                title="Peta Lokasi Pekerja"
                src={embedSrc}
                className="w-full h-[280px]"
                loading="lazy"
                tabIndex={-1}
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="detail-map-iframe"
              />
            </div>
          </div>

          <div className="space-y-3">
            <SectionTitle>Dokumen Pekerja</SectionTitle>
            <div className="flex flex-wrap items-center gap-2" data-testid="dokumen-toolbar">
              <Select value={jenis} onValueChange={setJenis}>
                <SelectTrigger className="h-9 w-40" data-testid="dokumen-jenis-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {JENIS_DOKUMEN.map((j) => <SelectItem key={j} value={j}>{j}</SelectItem>)}
                </SelectContent>
              </Select>
              <input type="file" ref={dokInputRef} accept="application/pdf,image/jpeg,image/png,image/webp"
                onChange={uploadDok} className="hidden" data-testid="dokumen-file-input" />
              <Button size="sm" variant="outline" onClick={() => dokInputRef.current?.click()}
                disabled={uploadingDok || readOnly} data-testid="upload-dokumen-button"
                className="gap-2 h-9 border-blue-200 text-blue-700 hover:bg-blue-50">
                {uploadingDok ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
                Unggah Dokumen
              </Button>
              <span className="text-[11px] text-slate-400">PDF/JPG/PNG/WEBP, maks 10 MB</span>
            </div>
            {dokumen.length === 0 ? (
              <p className="text-sm text-slate-400" data-testid="dokumen-empty">Belum ada dokumen tersimpan.</p>
            ) : (
              <ul className="space-y-2" data-testid="dokumen-list">
                {dokumen.map((d) => (
                  <li key={d.id} className="flex items-center gap-3 rounded-md border border-slate-100 bg-slate-50/60 px-3 py-2"
                    data-testid={`dokumen-item-${d.id}`}>
                    <FileText className="h-4 w-4 text-blue-500 shrink-0" />
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[11px] shrink-0">{d.jenis}</Badge>
                    <span className="text-sm font-medium text-slate-800 truncate flex-1">{d.nama_file}</span>
                    <span className="text-xs text-slate-400 shrink-0">{fmtUkuran(d.ukuran)}</span>
                    <button onClick={() => previewDok(d)} data-testid={`preview-dokumen-${d.id}`}
                      className="h-7 w-7 rounded-md flex items-center justify-center text-slate-500 hover:bg-blue-50 hover:text-blue-600 transition-colors">
                      <Eye className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => downloadDok(d)} data-testid={`download-dokumen-${d.id}`}
                      className="h-7 w-7 rounded-md flex items-center justify-center text-slate-500 hover:bg-emerald-50 hover:text-emerald-600 transition-colors">
                      <Download className="h-3.5 w-3.5" />
                    </button>
                    {!readOnly && <button onClick={() => deleteDok(d)} data-testid={`delete-dokumen-${d.id}`}
                      className="h-7 w-7 rounded-md flex items-center justify-center text-slate-500 hover:bg-rose-50 hover:text-rose-600 transition-colors">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="space-y-3">
            <SectionTitle>Riwayat Perubahan</SectionTitle>
            {logs === null ? (
              <div className="py-4 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div>
            ) : logs.length === 0 ? (
              <p className="text-sm text-slate-400" data-testid="detail-history-empty">Belum ada riwayat perubahan.</p>
            ) : (
              <div className="space-y-2 max-h-56 overflow-y-auto pr-1" data-testid="detail-history-list">
                {logs.map((l) => {
                  const meta = ACTION_META[l.action] || { label: l.action, tone: "bg-slate-50 text-slate-700 border-slate-200" };
                  return (
                    <div key={l.id} className="rounded-md border border-slate-100 bg-slate-50/60 p-3" data-testid={`detail-history-${l.id}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className={`${meta.tone} font-medium text-[11px]`}>{meta.label}</Badge>
                        <span className="text-xs text-slate-500">oleh {l.user_name}</span>
                        <span className="text-xs text-slate-400 ml-auto">{formatWaktu(l.timestamp)}</span>
                      </div>
                      <ChangeList changes={l.changes} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-2 justify-end pt-2 border-t border-slate-100">
            <Button onClick={printCard} disabled={printing} variant="outline"
              className="gap-2 border-amber-200 text-amber-700 hover:bg-amber-50"
              data-testid="detail-print-card">
              {printing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
              Cetak Kartu
            </Button>
            <Button asChild variant="outline" className="gap-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
              data-testid="detail-open-map">
              <a href={externalHref} target="_blank" rel="noopener noreferrer">
                Buka di Google Maps <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
            {!readOnly && <Button onClick={() => onEdit(worker)} data-testid="detail-edit-button"
              className="gap-2 bg-blue-600 hover:bg-blue-700 text-white transition-colors">
              <Pencil className="h-4 w-4" /> Ubah Data
            </Button>}
          </div>
        </div>

        <Dialog open={!!preview} onOpenChange={(v) => !v && closePreview()}>
          <DialogContent className="max-w-4xl" data-testid="dokumen-preview-dialog">
            <DialogHeader>
              <DialogTitle className="text-base truncate pr-6">{preview?.jenis}: {preview?.nama}</DialogTitle>
              <DialogDescription>Pratinjau dokumen pekerja</DialogDescription>
            </DialogHeader>
            {preview && (preview.isPdf ? (
              <iframe title="Pratinjau Dokumen" src={preview.url}
                className="w-full h-[70vh] rounded-md border border-slate-200" data-testid="preview-iframe" />
            ) : (
              <div className="h-[70vh] w-full rounded-md border border-slate-200 bg-slate-900/90 overflow-hidden" data-testid="preview-image">
                <ZoomImage src={preview.url} alt={preview.nama} className="w-full h-full" />
              </div>
            ))}
            <a href={preview?.url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline" data-testid="preview-newtab">Buka di tab baru</a>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
